import {
    type BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useHistory, useRouteMatch } from "react-router";
import { Sortable } from "react-sortablejs";

import { BTButton } from "@btnet/core-components";
import type { BTLoginTypes } from "@btnet/services-btnet/handlers/models/bTLoginTypes";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { type MediaType } from "types/enum";

import { useResizeObserver } from "utilities/resizeObserver/useResizeObserver";

import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { canViewBTFileSystemInLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { usePanelAwareNavigate } from "commonComponents/btWrappers/BTSplitView/usePanelAwareNavigate";
import {
    getAttachmentOverlayItems,
    showDownload,
} from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";
import { AttachmentOverlay } from "commonComponents/entity/media/common/MediaContextMenu/Overlays/AttachmentOverlay";
import {
    MAX_ATTACHMENT_TILE_WIDTH,
    MediaAttachmentTile,
} from "commonComponents/entity/media/common/MediaGrid/MediaAttachmentTile/MediaAttachmentTile";
import { type SharingSettingsAPI } from "commonComponents/entity/sharing/SharingContainer/SharingContainer.types";

import {
    areAllFilesSelected,
    buildAttachedMediaPropertiesRouting,
    canMultiSelectFiles,
    downloadFromUrl,
    handleSelectAllFiles,
    hasSelectedFiles,
    isPDFFileType,
} from "entity/media/common/media.utils";
import { type AnnotationType } from "entity/media/common/mediaTypes";

interface IMediaGridProps {
    files: BTFileSystem[];
    isReadOnly?: boolean;
    sharingSettings?: SharingSettingsAPI;
    isShowingMore?: boolean;
    /**
     * Callback to maintain the state of the attachments grid view.
     * The parent component will keep track of whether or not the grid is fully expaned
     */
    onShowMore: (isShowingMore: boolean) => void;
    onOpenLightbox: (id: number) => void;
    /** Return true to suppress the default click handling (lightbox/route/download). */
    onFileClick?: (file: BTFileSystem) => boolean;
    onVideoDownload: (file: BTFileSystem) => Promise<void>;
    onFileDraw?: (
        docInstanceId: number,
        annotationType: AnnotationType,
        mediaType: MediaType,
    ) => Promise<void>;
    onEditOnline?: (docInstanceId: number, mediaType: MediaType) => Promise<void>;
    onFileDelete?: (file: BTFileSystem) => Promise<void>;
    onViewPermissionsUpdate?: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    /**
     * Options from the entity attached files used for determining if the show/hide menu items should be displayed
     */
    fileViewOptions?: FileViewOptions;
    entityId: number;
    entityName: string;
    isOnExternalPage?: boolean;
    onSetMainPhoto?: (file: BTFileSystem) => Promise<void>;
    selectedFileIds?: number[];
    onSelectedFileChange?: (fileId: number, isSelected: boolean) => void;
    /**
     * Array of file IDs that should not be deletable in the context menu.
     * The "Move to trash" menu item will be hidden for these files.
     */
    nonDeletableFileIds?: number[];
    onReorderFiles?: (files: BTFileSystem[]) => void;
}

export const MediaGrid = ({
    files,
    isShowingMore,
    isReadOnly,
    sharingSettings,
    fileViewOptions,
    onShowMore,
    onOpenLightbox,
    onFileClick,
    onVideoDownload,
    onFileDraw,
    onEditOnline,
    onFileDelete,
    onViewPermissionsUpdate,
    entityId,
    entityName,
    isOnExternalPage = false,
    onSetMainPhoto,
    selectedFileIds,
    onSelectedFileChange,
    nonDeletableFileIds,
    onReorderFiles,
}: IMediaGridProps) => {
    const userInfo = useContext(UserInfoContext);
    const history = useHistory();
    const match = useRouteMatch();
    const webview = useWebviewContext();
    const { querySuffix } = usePanelAwareNavigate();

    const [showAllFiles, setShowAllFiles] = useState(false);
    const [maxVisibleFiles, setMaxVisibleFiles] = useState<number>(1);
    const [scrollPosition, setScrollPosition] = useState<number>(0);

    const gridSortableRef = useRef<HTMLDivElement | null>(null);
    const filesRef = useRef(files);
    useLayoutEffect(() => {
        filesRef.current = files;
    }, [files]);

    const isControlled = isShowingMore !== undefined && onShowMore !== undefined;
    const [showFiles, setShowFiles] = isControlled
        ? [!!isShowingMore, onShowMore as Dispatch<SetStateAction<boolean>>]
        : [showAllFiles, setShowAllFiles];

    const calculateInternal = useCallback((entry?: ResizeObserverEntry) => {
        const width = entry?.target?.clientWidth ?? 0;
        const itemsPerRow = Math.max(Math.floor(width / MAX_ATTACHMENT_TILE_WIDTH), 1);
        setMaxVisibleFiles(itemsPerRow);
    }, []);
    const [, onDivRefChange] = useResizeObserver<HTMLDivElement>(calculateInternal);

    const setGridRef = useCallback(
        (el: HTMLDivElement | null) => {
            gridSortableRef.current = el;
            onDivRefChange(el);
        },
        [onDivRefChange],
    );

    useEffect(() => {
        if (!onReorderFiles || !gridSortableRef.current) return;
        const el = gridSortableRef.current;
        const sortable = Sortable.create(el, {
            handle: ".DragAndDropHandle",
            ghostClass: "Placeholder",
            dragClass: "DraggedItem",
            animation: 150,
            onEnd: () => {
                const items = Array.from(el.querySelectorAll("[data-file-id]"));
                const currentFiles = filesRef.current;
                const reordered = items
                    .map((item) => {
                        const id = Number(item.getAttribute("data-file-id"));
                        return currentFiles.find((f) => f.id === id);
                    })
                    .filter((f): f is BTFileSystem => f != null);
                if (reordered.length === currentFiles.length) {
                    onReorderFiles(reordered);
                }
            },
        });
        return () => sortable.destroy();
    }, [onReorderFiles]);

    useEffect(() => {
        if (!showFiles && files.length > maxVisibleFiles) {
            setShowFiles(false);
        }
        if (showFiles && files.length <= maxVisibleFiles) {
            setShowFiles(true);
        }
    }, [files.length, maxVisibleFiles, setShowFiles, showFiles]);

    const handleViewAll = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setScrollPosition(window.scrollY);
            setShowFiles(true);
        },
        [setShowFiles],
    );

    const handleViewLess = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setShowFiles(false);
            window.scrollTo(0, scrollPosition);
        },
        [scrollPosition, setShowFiles],
    );

    const handleFileClick = useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>, file: BTFileSystem) => {
            // stop row from being selected
            e.stopPropagation();

            if (onFileClick?.(file)) {
                return;
            }

            const canViewInLightbox = canViewBTFileSystemInLightbox(file);
            if (canViewInLightbox) {
                onOpenLightbox(file.id);
            } else if (file.isTempFile || entityId === 0) {
                // We can't download or open the file in a new tab if it is a temp file
                return;
            } else if (isOnExternalPage) {
                if (isPDFFileType(file.extension)) {
                    window.open(file.docPath, "_blank");
                } else if (showDownload(file, undefined, !!webview)) {
                    downloadFromUrl(file.downloadDocPath, file.title);
                }
            } else {
                const link = buildAttachedMediaPropertiesRouting(
                    { fileId: file.id, mediaType: file.mediaType, jobId: file.jobsiteId },
                    entityName,
                    entityId,
                );
                history.push(match.url + link + querySuffix);
            }
        },
        [
            entityId,
            entityName,
            history,
            match.url,
            onOpenLightbox,
            onFileClick,
            isOnExternalPage,
            webview,
            querySuffix,
        ],
    );

    const viewableFiles = showFiles ? files : files.slice(0, maxVisibleFiles);
    const showViewAll = viewableFiles.length !== files.length && !showFiles;
    const showViewLess = viewableFiles.length > maxVisibleFiles && showFiles;

    const canSelectFiles = canMultiSelectFiles(files, isOnExternalPage, isReadOnly);
    const filesAreSelected = hasSelectedFiles(selectedFileIds);
    const allFilesSelected = areAllFilesSelected(files, selectedFileIds);

    const handleSelectAll = useCallback(() => {
        if (onSelectedFileChange) {
            handleSelectAllFiles(files, selectedFileIds, onSelectedFileChange);
        }
    }, [files, selectedFileIds, onSelectedFileChange]);

    return (
        <>
            {canSelectFiles && viewableFiles.length > 0 && (
                <div className="margin-bottom-xs">
                    <BTCheckbox
                        id="MediaGridSelectAllFilesCheckbox"
                        data-testid="MediaGridSelectAllFilesCheckbox"
                        checked={allFilesSelected}
                        indeterminate={filesAreSelected && !allFilesSelected}
                        onClick={handleSelectAll}
                    >
                        Select all
                    </BTCheckbox>
                </div>
            )}
            <BTRow gutter={[8, 8]} ref={setGridRef} data-testid="mediaGrid" className="media-grid">
                {viewableFiles.map((file) => {
                    const items = getAttachmentOverlayItems(
                        file,
                        !!isReadOnly,
                        userInfo?.loginType,
                        sharingSettings,
                        true,
                        !onFileDraw || isOnExternalPage,
                        !onEditOnline,
                        fileViewOptions,
                        isOnExternalPage,
                        nonDeletableFileIds,
                        !!webview,
                    );

                    return (
                        <MediaAttachmentTile
                            key={`mediaGrid-file-${file.id}`}
                            file={file}
                            entityId={entityId}
                            fileViewOptions={fileViewOptions}
                            isSelected={selectedFileIds?.includes(file.id)}
                            isOnExternalPage={isOnExternalPage}
                            isReadOnly={isReadOnly}
                            showDragHandle={!!onReorderFiles && !showViewAll}
                            onSelectedChanged={onSelectedFileChange}
                            onFileClick={(e) => handleFileClick(e, file)}
                            contextMenuProps={{
                                renderOverlay: (onMenuClick) => (
                                    <AttachmentOverlay
                                        file={file}
                                        items={items}
                                        isReadOnly={isReadOnly}
                                        onClickDownloadVideo={onVideoDownload}
                                        onClickDraw={onFileDraw}
                                        onClickEditOnline={onEditOnline}
                                        onClickDelete={onFileDelete}
                                        onFilePermissionsUpdate={onViewPermissionsUpdate}
                                        onMenuClick={onMenuClick}
                                        onClickSetMainPhoto={onSetMainPhoto}
                                    />
                                ),
                                items,
                            }}
                        />
                    );
                })}
            </BTRow>
            {(showViewAll || showViewLess) && (
                <BTRow justify="center">
                    {showViewAll && (
                        <BTButton
                            data-testid="viewAllFiles"
                            type="tertiary"
                            onClick={handleViewAll}
                        >
                            {`View all (${files.length})`}
                        </BTButton>
                    )}
                    {showViewLess && (
                        <BTButton
                            data-testid="viewLessFiles"
                            type="tertiary"
                            onClick={handleViewLess}
                        >
                            View less
                        </BTButton>
                    )}
                </BTRow>
            )}
        </>
    );
};
